import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  InputAdornment,
  Avatar,
  Stack,
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  Cancel,
  TrendingUp,
  TrendingDown,
  Euro,
  AttachMoney,
  Warning,
  Business,
  Gavel,
  Search,
  Info,
  Image,
  PendingActions,
  Close,
  Flip,
  Star,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import UserService from '../../services/userService';
import CreditService from '../../services/creditService';
import CheckImage from '../../components/credit/CheckImage';
import useResolvedUserId from '../../hooks/useResolvedUserId';

const Approvals = ({ user }) => {
  const theme = useTheme();
  const [sellerId, setSellerId] = useState(user?.userId?.toString() || '');
  const [sellerName, setSellerName] = useState('');
  const [credits, setCredits] = useState([]);
  const [transferredCredits, setTransferredCredits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTransferred, setLoadingTransferred] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', credit: null });
  const [checkDialog, setCheckDialog] = useState(false);
  const [selectedCreditForCheck, setSelectedCreditForCheck] = useState(null);
  const [isCheckFlipped, setIsCheckFlipped] = useState(false);
  const [selectedCustomerForTransfer, setSelectedCustomerForTransfer] = useState(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const checkImageRef = useRef(null);
  const [autoSellerIban, setAutoSellerIban] = useState('');
  const [searchCustomer, setSearchCustomer] = useState('');
  const [hasTransferHistory, setHasTransferHistory] = useState(false);
  const [guaranteeFilter, setGuaranteeFilter] = useState('all'); // 'all', 'guaranteed', 'non-guaranteed'
  const [myPayments, setMyPayments] = useState([]); // Payments'teki approved payment'lar
  const currentUserId = useResolvedUserId(user);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Clear search when switching tabs
    setSearchCustomer('');
    // Load transferred credits when switching to transferred tab
    if (newValue === 3 && transferredCredits.length === 0 && !loadingTransferred) {
      fetchTransferredCredits();
    }
  };

  const fetchSellerName = async (id) => {
    if (!id) return;
    const result = await UserService.getCustomerNameByUserId(id);
    if (result.success) {
      setSellerName(result.data.customerName);
      setError('');
    } else {
      setSellerName('');
      setError(result.message);
    }
  };

  const fetchCredits = async (id) => {
    if (!id) return;
    setLoading(true);
    setError('');
    const result = await CreditService.getCreditsBySeller(id);
    if (result.success) {
      setCredits(result.data);
    } else {
      setError(result.message);
      setCredits([]);
    }
    setLoading(false);
  };

  const fetchTransferredCredits = async () => {
    setLoadingTransferred(true);
    setError('');
    const result = await CreditService.getTransferredCreditsBySeller();
    if (result.success) {
      setTransferredCredits(result.data);
    } else {
      setError(result.message);
      setTransferredCredits([]);
    }
    setLoadingTransferred(false);
  };

  const fetchSellerInfo = async (id) => {
    if (!id) return;
    await Promise.all([fetchSellerName(id), fetchCredits(id)]);
  };

  //Payments'teki approved payment'ları çek
  const fetchMyPayments = async (sellerUserId) => {
    if (!sellerUserId) return;
    try {
      // Hem buyer hem seller olarak oluşturduğu active payment'ları al
      const [byBuyer, bySeller] = await Promise.all([
        CreditService.getCreditsByBuyer(sellerUserId),
        CreditService.getCreditsBySeller(sellerUserId),
      ]);

      const approvedBuyer = byBuyer.success
        ? (byBuyer.data || []).filter(credit => credit.status?.toLowerCase() === 'active')
        : [];
      const approvedSeller = bySeller.success
        ? (bySeller.data || []).filter(credit => credit.status?.toLowerCase() === 'active')
        : [];

      // Tekilleştir (id üzerinden)
      const all = [...approvedBuyer, ...approvedSeller];
      const unique = [];
      const seen = new Set();
      all.forEach(c => {
        if (!seen.has(c.id)) {
          seen.add(c.id);
          unique.push(c);
        }
      });

      setMyPayments(unique);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setMyPayments([]);
    }
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const parts = formatter.formatToParts(amount);
    let formatted = '';
    for (const part of parts) {
      if (part.type === 'currency') {
        formatted += part.value + ' ';
      } else {
        formatted += part.value;
      }
    }
    return formatted.trim();
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status.toLowerCase()) {
      case 'active': return 'Approved';
      case 'pending': return 'Pending';
      case 'rejected': return 'Rejected';
      case 'closed': return 'Closed';
      default: return status;
    }
  };

  const getFilteredCredits = () => {
    let filtered = [];
    if (activeTab === 0) {
      // Pending tab
      filtered = credits.filter(c => c.status.toLowerCase() === 'pending');
    } else if (activeTab === 1) {
      // Approved tab
      filtered = credits.filter(c => c.status.toLowerCase() === 'active');
    } else if (activeTab === 2) {
      // Rejected tab
      filtered = credits.filter(c => c.status.toLowerCase() === 'rejected');
    } else if (activeTab === 3) {
      // Transferred tab - return transferred credits
      filtered = transferredCredits;
    }
    
    // Apply guarantee filter
    if (guaranteeFilter === 'guaranteed') {
      filtered = filtered.filter(c => c.isGuaranteed === true);
    } else if (guaranteeFilter === 'non-guaranteed') {
      filtered = filtered.filter(c => c.isGuaranteed === false);
    }
    // 'all' - no filter applied
    
    // Apply search filter (searches in both Customer ID and Customer Name)
    if (searchCustomer && activeTab !== 3) {
      const searchTerm = searchCustomer.toLowerCase();
      filtered = filtered.filter(c => 
        c.buyerId?.toString().toLowerCase().includes(searchTerm) ||
        c.buyerName?.toLowerCase().includes(searchTerm)
      );
    } else if (searchCustomer && activeTab === 3) {
      const searchTerm = searchCustomer.toLowerCase();
      filtered = filtered.filter(c => 
        c.buyerId?.toString().toLowerCase().includes(searchTerm) ||
        c.buyerName?.toLowerCase().includes(searchTerm) ||
        c.transferInfo?.newSellerName?.toLowerCase().includes(searchTerm)
      );
    }
    
    // Sort by createdOn descending (newest first) or transferDate for transferred tab
    if (activeTab === 3) {
      return filtered.sort((a, b) => new Date(b.transferInfo?.transferDate) - new Date(a.transferInfo?.transferDate));
    }
    return filtered.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));
  };

  const getTotalAmount = () => {
    const filtered = getFilteredCredits();
    return filtered.reduce((sum, credit) => sum + credit.amount, 0);
  };

  const formatIbanForDisplay = (iban) => {
    if (!iban) return '';
    const cleanValue = iban.replace(/\s+/g, '').toUpperCase();
    return cleanValue.replace(/(.{4})/g, '$1 ').trim();
  };

  const resolveSellerIban = async (credit) => {
    // First check if credit has sellerIban
    const creditIban = credit?.sellerIban ? credit.sellerIban.replace(/\s+/g, '').toUpperCase() : '';
    if (creditIban) {
      return creditIban;
    }
    
    // If no credit IBAN, fetch from user profile API to get the latest IBAN
    try {
      const result = await UserService.getCurrentUserProfile();
      if (result.success && result.data) {
        const profile = result.data.data || result.data;
        const profileIban = profile?.ibanNumber ? profile.ibanNumber.replace(/\s+/g, '').toUpperCase() : '';
        if (profileIban) {
          return profileIban;
        }
      }
    } catch (error) {
      console.error('Error fetching user profile for IBAN:', error);
    }
    
    // Fallback to user object (may be outdated)
    const userIban = user?.ibanNumber ? user.ibanNumber.replace(/\s+/g, '').toUpperCase() : '';
    return userIban || '';
  };

  const handleAction = async (credit, action) => {
    if (action === 'approve') {
      // Fetch IBAN from API to ensure we have the latest data
      const resolvedIban = await resolveSellerIban(credit);
      setAutoSellerIban(resolvedIban);
    } else {
      setAutoSellerIban('');
    }
    setConfirmDialog({ open: true, action: action, credit: credit });
  };

  const confirmAction = async () => {
    const { action, credit } = confirmDialog;
    
    let sellerIbanToSend = null;
    if (action === 'approve') {
      sellerIbanToSend = autoSellerIban?.trim().toUpperCase() || null;
      if (!sellerIbanToSend) {
        setError('Seller IBAN not found. Please update your IBAN in the Profile page before approving.');
        return;
      }
      if (sellerIbanToSend.length < 15) {
        setError('Seller IBAN must contain at least 15 characters.');
        return;
      }
    }

    const reviewData = { 
      creditId: credit.id, 
      action: action,
      sellerIban: sellerIbanToSend
    };
    const result = await CreditService.processReview(reviewData);
    if (result.success) {
      await fetchCredits(sellerId);
      setConfirmDialog({ open: false, action: '', credit: null });
      setAutoSellerIban('');
    } else {
      setError(result.message);
    }
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, action: '', credit: null });
    setAutoSellerIban('');
  };

  useEffect(() => {
    if (!currentUserId) return;

    setSellerId(currentUserId.toString());
    fetchSellerInfo(currentUserId);
    fetchTransferredCredits();
    fetchMyPayments(currentUserId);
  }, [currentUserId]);

  return (
    <Box sx={{ minHeight: '100vh', py: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ width: 64, height: 64, background: 'rgba(255,255,255,0.25)', fontSize: '1.5rem', fontWeight: 600, border: '3px solid rgba(255,255,255,0.3)' }}>
                  {user?.name && user?.surname ? `${user.name.charAt(0)}${user.surname.charAt(0)}`.toUpperCase() : user?.name ? user.name.charAt(0).toUpperCase() : 'S'}
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>{sellerName || 'Seller Name'}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Seller ID: {sellerId}</Typography>
              </Grid>
              <Grid item>
                <Chip 
                  label="SELLER ACCOUNT" 
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)', 
                    color: 'white', 
                    fontWeight: 600, 
                    border: '1px solid rgba(255,255,255,0.3)', 
                    mr: 2,
                    height: 36.5,
                    borderRadius: 1,
                    '& .MuiChip-label': {
                      px: 2
                    }
                  }} 
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={(loading || loadingTransferred) ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Refresh />}
                  onClick={() => {
                    fetchSellerInfo(sellerId);
                    fetchTransferredCredits();
                  }}
                  disabled={!sellerId || loading || loadingTransferred}
                  sx={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.3)',
                    height: 36.5,
                    '&:hover': {
                      background: 'rgba(255,255,255,0.3)',
                    }
                  }}
                >
                  {(loading || loadingTransferred) ? 'Loading...' : 'Refresh'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>
            
            {/* Search and Filter */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ mb: 2, p: 3, backgroundColor: 'grey.50', borderRadius: 3 }}>
                <TextField 
                  fullWidth 
                  placeholder="Search by Customer ID or Name..."
                  value={searchCustomer}
                  onChange={(e) => setSearchCustomer(e.target.value)}
                  variant="outlined" 
                  size="small" 
                  InputProps={{ 
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="primary" />
                      </InputAdornment>
                    ) 
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      '&:hover fieldset': { borderColor: '#1976d2' },
                      '&.Mui-focused fieldset': { borderColor: '#1976d2' }
                    }
                  }}
                />
              </Box>
              {/* Guarantee Filter */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                  Filter:
                </Typography>
                <Chip
                  label="All"
                  onClick={() => setGuaranteeFilter('all')}
                  color={guaranteeFilter === 'all' ? 'primary' : 'default'}
                  size="small"
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="Guaranteed"
                  onClick={() => setGuaranteeFilter('guaranteed')}
                  color={guaranteeFilter === 'guaranteed' ? 'primary' : 'default'}
                  size="small"
                  icon={<Star sx={{ fontSize: '0.875rem !important' }} />}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="Non-Guaranteed"
                  onClick={() => setGuaranteeFilter('non-guaranteed')}
                  color={guaranteeFilter === 'non-guaranteed' ? 'primary' : 'default'}
                  size="small"
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Credits Table */}
            <Card elevation={8} sx={{ borderRadius: 3 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 3, background: 'linear-gradient(to right, rgba(25, 118, 210, 0.02) 0%, rgba(25, 118, 210, 0.05) 100%)' }}>
                <Tabs value={activeTab} onChange={handleTabChange} sx={{ '& .MuiTab-root': { textTransform: 'none', fontSize: '1rem', fontWeight: 500, minHeight: 48, '&.Mui-selected': { color: '#1976d2', fontWeight: 600 } }, '& .MuiTabs-indicator': { backgroundColor: '#1976d2', height: 3, borderRadius: '3px 3px 0 0' } }}>
                  <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PendingActions fontSize="small" />Pending<Chip label={credits.filter(c => c.status.toLowerCase() === 'pending').length} size="small" color="warning" sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }} /></Box>} />
                  <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CheckCircle fontSize="small" />Approved<Chip label={credits.filter(c => c.status.toLowerCase() === 'active').length} size="small" color="success" sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }} /></Box>} />
                  <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Cancel fontSize="small" />Rejected<Chip label={credits.filter(c => c.status.toLowerCase() === 'rejected').length} size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }} /></Box>} />
                  <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><TrendingUp fontSize="small" />Transferred<Chip label={transferredCredits.length} size="small" color="info" sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }} /></Box>} />
                </Tabs>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Payment Number</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Customer ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Customer Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Create Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Payment Date</TableCell>
                      {activeTab === 3 && <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Transferred To</TableCell>}
                      {activeTab === 3 && <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>Transfer Date</TableCell>}
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.875rem' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center', fontSize: '0.875rem' }}>View</TableCell>
                      {activeTab !== 3 && <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center', fontSize: '0.875rem' }}>Action</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(loading || (activeTab === 3 && loadingTransferred)) ? (
                      <TableRow>
                        <TableCell colSpan={activeTab === 3 ? 10 : 9} sx={{ textAlign: 'center', py: 8 }}>
                          <CircularProgress size={40} sx={{ color: '#1976d2' }} />
                          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontSize: '0.875rem' }}>Loading {activeTab === 3 ? 'transferred credits' : 'approval requests'}...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : getFilteredCredits().length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={activeTab === 3 ? 10 : 9} sx={{ textAlign: 'center', py: 8 }}>
                          <Box sx={{ display: 'inline-flex', p: 3, borderRadius: 3, backgroundColor: 'grey.100', mb: 2 }}><Gavel sx={{ fontSize: 48, color: 'grey.400' }} /></Box>
                          <Typography variant="h6" sx={{ mb: 1, color: 'text.primary', fontSize: '1rem' }}>{activeTab === 3 ? 'No Transferred Credits' : 'No Approval Requests'}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>{activeTab === 3 ? 'You have not transferred any payment guarantees yet.' : (sellerId ? 'No credits found for this seller.' : 'Enter a Seller ID to view requests.')}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      getFilteredCredits().map((credit) => {
                        return (
                          <TableRow key={credit.id} hover sx={{ '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)', borderLeftColor: activeTab === 3 ? 'info.main' : (credit.status.toLowerCase() === 'pending' ? 'warning.main' : credit.status.toLowerCase() === 'active' ? 'success.main' : 'grey.300') }, borderLeft: '4px solid transparent' }}>
                            <TableCell sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                              {credit.paymentNumber || '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>
                              {credit.isGuaranteed ? (
                                <Chip
                                  label="Guaranteed"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    backgroundColor: '#FFC107',
                                    color: '#000',
                                    fontWeight: 600,
                                  }}
                                  icon={<Star sx={{ fontSize: '0.75rem !important', color: '#000 !important' }} />}
                                />
                              ) : (
                                <Chip
                                  label="Non-Guaranteed"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    backgroundColor: '#E0E0E0',
                                    color: '#666',
                                    fontWeight: 500,
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500, fontSize: '0.8rem' }}>{credit.buyerId}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>{credit.buyerName}</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{new Date(credit.createdOn).toLocaleDateString('en-US')}</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{credit.validityDate ? new Date(credit.validityDate).toLocaleDateString('en-US') : '-'}</TableCell>
                            {activeTab === 3 && (
                              <>
                                <TableCell sx={{ fontWeight: 600, color: 'info.main', fontSize: '0.8rem' }}>{credit.transferInfo?.newSellerName || '-'}</TableCell>
                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{credit.transferInfo?.transferDate ? new Date(credit.transferInfo.transferDate).toLocaleDateString('en-US') : '-'}</TableCell>
                              </>
                            )}
                            <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>{formatCurrency(credit.amount, credit.currency)}</TableCell>
                            <TableCell sx={{ textAlign: 'center', fontSize: '0.8rem' }}>
                              <Tooltip title="View Check Image">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={async () => {
                                    setSelectedCreditForCheck(credit);
                                    setCheckDialog(true);
                                    // Check if transfer history exists for buyer
                                    if (currentUserId === credit.buyerId && credit.status?.toLowerCase() === 'active') {
                                      const historyResult = await CreditService.getTransferHistory(credit.id);
                                      setHasTransferHistory(historyResult.success && historyResult.data && historyResult.data.length > 0);
                                    } else {
                                      setHasTransferHistory(false);
                                    }
                                  }}
                                  sx={{
                                    color: 'primary.main',
                                    backgroundColor: 'primary.lighter',
                                    '&:hover': {
                                      backgroundColor: 'primary.main',
                                      color: 'white',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <Image fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            {activeTab !== 3 && (
                              <TableCell sx={{ textAlign: 'center', fontSize: '0.8rem' }}>
                                {credit.status.toLowerCase() === 'pending' ? (
                                  <Stack direction="row" spacing={1} justifyContent="center">
                                    <Tooltip title="Approve">
                                      <IconButton size="small" onClick={() => handleAction(credit, 'approve')} sx={{ color: 'success.main', backgroundColor: 'success.lighter', '&:hover': { backgroundColor: 'success.main', color: 'white', transform: 'scale(1.1)' }, transition: 'all 0.2s' }}>
                                        <CheckCircle fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Reject">
                                      <IconButton size="small" onClick={() => handleAction(credit, 'reject')} sx={{ color: 'error.main', backgroundColor: 'error.lighter', '&:hover': { backgroundColor: 'error.main', color: 'white', transform: 'scale(1.1)' }, transition: 'all 0.2s' }}>
                                        <Cancel fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                ) : (
                                  <Chip label={getStatusText(credit.status)} color={getStatusColor(credit.status)} size="small" sx={{ fontWeight: 600, borderRadius: 2, color: 'white', fontSize: '0.75rem', height: 24 }} />
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {getFilteredCredits().length > 0 && activeTab !== 3 && (
                <Box sx={{ p: 3, borderTop: 2, borderColor: 'divider', background: 'linear-gradient(to right, rgba(25, 118, 210, 0.02) 0%, rgba(25, 118, 210, 0.05) 100%)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.875rem' }}>
                      {activeTab === 0 ? 'Pending' : activeTab === 1 ? 'Approved' : 'Rejected'} Total Amount:
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.25rem' }}>{formatCurrency(getTotalAmount(), credits[0]?.currency || 'EUR')}</Typography>
                  </Box>
                </Box>
              )}
            </Card>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog} PaperProps={{ sx: { borderRadius: 3, minWidth: 400 } }}>
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1, color: confirmDialog.action === 'approve' ? 'success.main' : 'error.main' }}>
          {confirmDialog.action === 'approve' ? 'Approve Payment Guarantee' : 'Reject Payment Guarantee'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.credit && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Are you sure you want to <strong>{confirmDialog.action === 'approve' ? 'approve' : 'reject'}</strong> the payment guarantee request for <strong>{formatCurrency(confirmDialog.credit.amount, confirmDialog.credit.currency)}</strong> from <strong>{confirmDialog.credit.buyerName}</strong>?
                </Typography>
                {confirmDialog.action === 'approve' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                      Payout IBAN
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: autoSellerIban ? 'text.primary' : 'error.main' }}>
                      {autoSellerIban ? formatIbanForDisplay(autoSellerIban) : 'No IBAN on file'}
                    </Typography>
                    {!autoSellerIban && (
                      <Typography variant="caption" color="error.main">
                        Please add your IBAN from the Profile page before approving.
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeConfirmDialog} variant="outlined" sx={{ borderRadius:2 }}>Cancel</Button>
          <Button onClick={confirmAction} color={confirmDialog.action === 'approve' ? 'success' : 'error'} variant="contained" autoFocus sx={{color:'white', borderRadius:2,background: confirmDialog.action === 'approve' ? 'linear-gradient(135deg, #00c853 0%, #00a726 100%)' : 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)' }}>
            {confirmDialog.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

        {/* Check Image Dialog */}
        <Dialog
          open={checkDialog}
          onClose={() => {
            setCheckDialog(false);
            setSelectedCreditForCheck(null);
            setHasTransferHistory(false);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              py: 2.5,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
              View
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedCreditForCheck && selectedCreditForCheck.status?.toLowerCase() === 'active' && (
                ((currentUserId === selectedCreditForCheck.sellerId || 
                  (currentUserId === selectedCreditForCheck.buyerId && selectedCreditForCheck.status?.toLowerCase() === 'active' && hasTransferHistory)) || 
                 (activeTab === 3 && selectedCreditForCheck.transferInfo)) && (
                  <Button
                    variant={isCheckFlipped ? 'contained' : 'outlined'}
                    startIcon={<Flip />}
                    onClick={() => setIsCheckFlipped(!isCheckFlipped)}
                    sx={{
                      background: isCheckFlipped 
                        ? 'rgba(255, 255, 255, 0.2)' 
                        : 'transparent',
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    {activeTab === 3 ? 'Transfer History' : (currentUserId === selectedCreditForCheck.sellerId ? 'Transfer Receivable' : 'Transfer History')}
                  </Button>
                )
              )}
              <IconButton
                onClick={() => {
                  setCheckDialog(false);
                  setSelectedCreditForCheck(null);
                  setIsCheckFlipped(false);
                  setHasTransferHistory(false);
                }}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {selectedCreditForCheck && (
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: '#fafafa',
                }}
              >
                <CheckImage
                  ref={checkImageRef}
                  checkData={{
                    amount: parseFloat(selectedCreditForCheck.originalAmount || selectedCreditForCheck.amount) || 0,
                    currency: selectedCreditForCheck.currency || 'EUR',
                    buyerName: selectedCreditForCheck.buyerName || 'Buyer Name',
                    sellerName: sellerName || selectedCreditForCheck.sellerName || 'Seller Name',
                    buyerIban: selectedCreditForCheck.buyerIban || 'TR00 0000 0000 0000 0000 0000 00',
                    dueDate: selectedCreditForCheck.validityDate,
                    checkNumber: selectedCreditForCheck.paymentNumber || '',
                    paymentNumber: selectedCreditForCheck.paymentNumber || '',
                    isGuaranteed: selectedCreditForCheck.isGuaranteed || false,
                    description: selectedCreditForCheck.description || '',
                    creatorName: user?.name && user?.surname ? `${user.name} ${user.surname}` : user?.name || '',
                  }}
                  preview={true}
                  showTemplate={true}
                  enableTransfer={activeTab === 1 && selectedCreditForCheck.status?.toLowerCase() === 'active' && currentUserId === selectedCreditForCheck.sellerId}
                  showTransferHistory={
                    (activeTab === 3 && selectedCreditForCheck.transferInfo) || 
                    (selectedCreditForCheck.status?.toLowerCase() === 'active' && currentUserId === selectedCreditForCheck.buyerId)
                  }
                  creditId={selectedCreditForCheck.id}
                  currentUserId={currentUserId}
                  currentSellerId={selectedCreditForCheck.sellerId}
                  currentBuyerId={selectedCreditForCheck.buyerId}
                  receivedAmount={selectedCreditForCheck.isPartialTransfer ? parseFloat(selectedCreditForCheck.amount) || 0 : null}
                  isFlipped={isCheckFlipped}
                  onFlipChange={(flipped) => setIsCheckFlipped(flipped)}
                  onSelectedCustomerChange={(customer) => {
                    setSelectedCustomerForTransfer(customer);
                  }}
                  myPayments={myPayments}
                  onTransferApprove={(transferData) => {
                    toast.success(`Credit transferred successfully to ${transferData.newSellerName}. This payment guarantee has been removed from your list and is now visible to ${transferData.newSellerName}.`);
                    setSelectedCustomerForTransfer(null);
                    setIsTransferring(false);
                    setIsCheckFlipped(false);
                    setCheckDialog(false);
                    setHasTransferHistory(false);
                    // Refresh credits list - the credit will no longer appear for the old seller
                    if (currentUserId) {
                      fetchCredits(currentUserId);
                      fetchTransferredCredits(); // Also refresh transferred credits list
                    }
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 0 }}>
            {selectedCreditForCheck && selectedCreditForCheck.status?.toLowerCase() === 'active' && 
             isCheckFlipped && 
             (currentUserId === selectedCreditForCheck.sellerId || (currentUserId === selectedCreditForCheck.buyerId && selectedCreditForCheck.status?.toLowerCase() === 'active') || (activeTab === 3 && selectedCreditForCheck.transferInfo)) ? (
              <>
                {activeTab !== 3 && (
                  <>
                    <Button
                      onClick={() => {
                        setIsCheckFlipped(false);
                        setSelectedCustomerForTransfer(null);
                      }}
                      variant="outlined"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                      }}
                    >
                      Cancel
                    </Button>
                    {activeTab === 1 && currentUserId === selectedCreditForCheck.sellerId && (
                      <Button
                        onClick={async () => {
                          if (checkImageRef.current) {
                            setIsTransferring(true);
                            try {
                              await checkImageRef.current.approve();
                            } catch (error) {
                              console.error('Transfer error:', error);
                            } finally {
                              setIsTransferring(false);
                            }
                          }
                        }}
                        variant="contained"
                        startIcon={isTransferring ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                        disabled={!selectedCustomerForTransfer || isTransferring}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                          },
                          '&:disabled': {
                            background: '#cbd5e1',
                            color: '#94a3b8',
                          },
                        }}
                      >
                        {isTransferring ? 'Transferring...' : 'Approve Transfer'}
                      </Button>
                    )}
                  </>
                )}
                {activeTab === 3 && (
                  <Button
                    onClick={() => {
                      setCheckDialog(false);
                      setSelectedCreditForCheck(null);
                      setIsCheckFlipped(false);
                      setSelectedCustomerForTransfer(null);
                      setHasTransferHistory(false);
                    }}
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 3 }}
                  >
                    Close
                  </Button>
                )}
              </>
            ) : (
              <Button
                onClick={() => {
                  setCheckDialog(false);
                  setSelectedCreditForCheck(null);
                  setIsCheckFlipped(false);
                  setSelectedCustomerForTransfer(null);
                }}
                variant="contained"
                color="primary"
                sx={{ borderRadius: 3 }}
              >
                Close
              </Button>
            )}{selectedCreditForCheck && selectedCreditForCheck.status.toLowerCase() === 'pending' ? (
              <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button
                  onClick={() => {
                    setCheckDialog(false);
                    handleAction(selectedCreditForCheck, 'reject');
                    setSelectedCreditForCheck(null);
                    setHasTransferHistory(false);
                  }}
                  startIcon={<Cancel />}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: '#f44336',
                    color: 'white',
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    '&:hover': {
                      backgroundColor: '#d32f2f',
                    },
                  }}
                >
                  Reject
                </Button>
                <Button
                  onClick={() => {
                    setCheckDialog(false);
                    handleAction(selectedCreditForCheck, 'approve');
                    setSelectedCreditForCheck(null);
                    setHasTransferHistory(false);
                  }}
                  startIcon={<CheckCircle />}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: '#4caf50',
                    color: 'white',
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    '&:hover': {
                      backgroundColor: '#388e3c',
                    },
                  }}
                >
                  Approve
                </Button>
              </Stack>
            ) : null}
          </DialogActions>
        </Dialog>
    </Box>
  );
};

export default Approvals;